import {
    isUnsupportedMarketplacePayload,
    type UnsupportedMarketplacePayload,
} from '@/shared/runtime-marketplace-unsupported';

export class UnsupportedMarketplaceRouteError extends Error {
    readonly code = 'unsupported_marketplace';
    readonly payload: UnsupportedMarketplacePayload;

    constructor(payload: UnsupportedMarketplacePayload) {
        super(payload.message);
        this.payload = payload;
    }
}

const readHeaderValue = (headers: unknown, name: string): string | null => {
    if (!headers || typeof headers !== 'object') {
        return null;
    }

    const headersWithGetter = headers as { get?: (headerName: string) => unknown };
    const fromGetter = typeof headersWithGetter.get === 'function' ? headersWithGetter.get(name) : null;
    if (typeof fromGetter === 'string' && fromGetter) {
        return fromGetter;
    }

    const record = headers as Record<string, unknown>;
    const directValue = record[name] ?? record[name.toLowerCase()] ?? record[name.toUpperCase()];
    if (typeof directValue === 'string') {
        return directValue;
    }

    if (Array.isArray(directValue) && typeof directValue[0] === 'string') {
        return directValue[0];
    }

    return null;
};

const isJsonContentType = (headers: unknown): boolean => {
    const contentType = readHeaderValue(headers, 'content-type');
    return typeof contentType === 'string' && contentType.toLowerCase().includes('application/json');
};

const toUnsupportedMarketplacePayload = (value: unknown): UnsupportedMarketplacePayload | null => {
    return isUnsupportedMarketplacePayload(value) ? value : null;
};

export const parseUnsupportedMarketplaceFetchResponse = async (
    response: Response,
): Promise<UnsupportedMarketplacePayload | null> => {
    if (response.status !== 400 || !isJsonContentType(response.headers)) {
        return null;
    }

    try {
        const payload = (await response.clone().json()) as unknown;
        return toUnsupportedMarketplacePayload(payload);
    } catch {
        return null;
    }
};

const parseUnsupportedMarketplaceBlobPayload = async (
    value: unknown,
): Promise<UnsupportedMarketplacePayload | null> => {
    if (!value) {
        return null;
    }

    if (typeof value === 'object' && isUnsupportedMarketplacePayload(value)) {
        return value;
    }

    if (typeof Blob !== 'undefined' && value instanceof Blob) {
        try {
            const parsed = JSON.parse(await value.text()) as unknown;
            return toUnsupportedMarketplacePayload(parsed);
        } catch {
            return null;
        }
    }

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value) as unknown;
            return toUnsupportedMarketplacePayload(parsed);
        } catch {
            return null;
        }
    }

    return null;
};

export const parseUnsupportedMarketplaceAxiosBlobResponse = async ({
    status,
    headers,
    data,
}: {
    status: number;
    headers: unknown;
    data: unknown;
}): Promise<UnsupportedMarketplacePayload | null> => {
    if (status !== 400 || !isJsonContentType(headers)) {
        return null;
    }

    return await parseUnsupportedMarketplaceBlobPayload(data);
};
